/**
 * Measurement summary statistics — the pass/fail + worst-deviation aggregation
 * over a set of point-of-measure (POM) readings.
 *
 * Deliberately dependency-free (no db, no other domain infra) so the SPA measure
 * tab, the tech-pack PDF, and QC can all summarise measurements from ONE source
 * without bundling domain infra — mirrors `garment/designs/flat-svg`. Exposed via
 * the `@flint/domain/garment/measurements/stats` deep export for that reason.
 *
 * Per-reading `inTolerance` / `deviationMm` are computed upstream (the
 * `MeasurementView` read-model); this only aggregates them.
 */

/** The minimal per-reading shape the aggregation needs (a subset of
 *  `MeasurementView`); kept local so this module stays import-free. */
export interface MeasurementStatInput {
  pomCode: string;
  /** actual − spec (signed, mm). */
  deviationMm: number;
  /** |deviation| ≤ tolerance. */
  inTolerance: boolean;
}

export interface MeasurementStats {
  total: number;
  passing: number;
  /** Whole-percent pass rate; 100 when there are no readings. */
  passRate: number;
  /** POM with the largest |deviation|, or null when empty. */
  worstPom: string | null;
  /** Largest |deviation| in mm; 0 when empty. */
  worstDevMm: number;
}

/** Aggregate pass/fail + worst-deviation stats over a set of POM readings. Pure. */
export function computeMeasurementStats(
  measurements: ReadonlyArray<MeasurementStatInput>,
): MeasurementStats {
  if (measurements.length === 0) {
    return { total: 0, passing: 0, passRate: 100, worstPom: null, worstDevMm: 0 };
  }
  let passing = 0;
  let worstPom: string | null = null;
  let worstDevMm = 0;
  for (const m of measurements) {
    if (m.inTolerance) passing++;
    const absDev = Math.abs(m.deviationMm);
    if (absDev > worstDevMm) {
      worstDevMm = absDev;
      worstPom = m.pomCode;
    }
  }
  return {
    total: measurements.length,
    passing,
    passRate: Math.round((passing / measurements.length) * 100),
    worstPom,
    worstDevMm,
  };
}
